import type {Encodable} from "./types.ts";

/* eslint-disable no-redeclare -- this is a function overload */
/**
 * Encode object as query parameters or x-www-form-urlencoded data.
 *
 * This is useful for replacing usages of `$.ajax`, which used this encoding to
 * add URL parameters to GET requests. For example,
 * ```ts
 *   $.ajax("/test", { data: { casing: "camel" } });
 * ```
 * can be replaced with
 * ```ts
 *   khanFetch(`/test?${encodeParams({ casing: "camel" })}`);
 * ```
 *
 * @param data An object. Note that the values of this object will simply be
 *        converted to strings, so you need to serialize them yourself
 *        manually. (Especially note that arrays probably won't do what you
 *        want)
 * @returns A string containing all of the key/value pairs urlencoded and
 *         concatenated.
 *
 * @example
 * encodeParams({ a: "hello", b: "world", c: 42 }) // -> "a=hello&b=world&c=42"
 */
export function encodeParams(
    data: Readonly<Record<string, Encodable | null | undefined>>,
): string;
/**
 * Encode object as query parameters or x-www-form-urlencoded data.
 *
 * @param data An array of [key, value] tuples. Note that the values of this
 *        object will simply be converted to strings, so you need to serialize
 *        them yourself manually. (Especially note that arrays probably won't
 *        do what you want)
 * @returns A string containing all of the key/value pairs urlencoded and
 *         concatenated.
 *
 * @example
 * encodeParams({ a: "hello", b: "world", c: 42 }) // -> "a=hello&b=world&c=42"
 */
export function encodeParams(
    data: ReadonlyArray<[string, Encodable | null | undefined]>,
): string;
export function encodeParams(
    data: ReadonlyArray<[string, Encodable | null | undefined]>,
): string;
export function encodeParams(
    data:
        | Readonly<Record<string, Encodable | null | undefined>>
        | ReadonlyArray<[string, Encodable | null | undefined]>,
): string {
    const iterable = Array.isArray(data) ? data : Object.entries(data);
    const urlSearchParams = new URLSearchParams();
    for (const [name, value] of iterable) {
        urlSearchParams.append(name, String(value));
    }
    return urlSearchParams.toString();
}
/* eslint-enable no-redeclare -- this is a function overload */
